import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Archive,
  DatabaseBackup,
  Trash2,
  ShieldAlert,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  History,
  X,
} from "lucide-react";

/**
 * SuperAdminBackupPanel
 * ----------------------------------------------------------------------
 * Console SuperAdmin pour :
 *   1) Sauvegarde globale de toute la plateforme (.zip)
 *   2) Sauvegarde "en chaîne" Admin -> Client -> Étudiant -> données
 *      associées, pour une sélection d'administrateurs (.zip)
 *   3) Nettoyage (purge définitive) de cette même chaîne, avec
 *      sauvegarde de sécurité automatique et confirmation explicite.
 *
 * Endpoints attendus (voir SuperAdminController.java) :
 *   GET  /api/v1/superadmin/accounts
 *   GET  /api/v1/superadmin/backup/global
 *   POST /api/v1/superadmin/backup/chaine     { adminIds }
 *   POST /api/v1/superadmin/nettoyage/chaine  { adminIds, sauvegarderAvant, forcer, supprimerAdmin }
 *
 * npm i lucide-react   (si pas déjà présent dans le projet)
 * Tailwind natif — aucune autre dépendance UI requise.
 */

const API_BASE = "/api/v1/superadmin";

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    let message = `Erreur ${res.status}`;
    try {
      const body = await res.json();
      message = body.error || message;
    } catch (e) {
      /* réponse non-JSON (ex: erreur 500 brute) */
	  console.error(e);
    }
    throw new Error(message);
  }
  return res;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function filenameFromDisposition(res, fallback) {
  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  return match ? match[1] : fallback;
}

function timestampedName(prefix) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${prefix}_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(
    d.getHours()
  )}${pad(d.getMinutes())}${pad(d.getSeconds())}.zip`;
}

function formatBytes(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const TYPE_LABELS = {
  GLOBALE: { label: "Globale", tone: "bg-teal-400/10 text-teal-300 ring-teal-400/30" },
  CHAINE: { label: "Chaîne", tone: "bg-sky-400/10 text-sky-300 ring-sky-400/30" },
  CHAINE_PRE_NETTOYAGE: { label: "Pré-nettoyage", tone: "bg-amber-400/10 text-amber-300 ring-amber-400/30" },
};

function TypeBadge({ type }) {
  const cfg = TYPE_LABELS[type] || { label: type, tone: "bg-slate-400/10 text-slate-300 ring-slate-400/30" };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------
// Badge de statut admin
// ---------------------------------------------------------------------
function StatutBadge({ actif }) {
  return actif ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300 ring-1 ring-inset ring-emerald-400/30">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> actif
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-400/10 px-2 py-0.5 text-[11px] font-medium text-slate-400 ring-1 ring-inset ring-slate-400/30">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500" /> suspendu
    </span>
  );
}

// ---------------------------------------------------------------------
// Carte d'action générique
// ---------------------------------------------------------------------
// eslint-disable-next-line
function ActionCard({ icon: Icon, tone, title, description, children }) {
  const toneRing = {
    teal: "ring-teal-400/20 hover:ring-teal-400/40",
    amber: "ring-amber-400/20 hover:ring-amber-400/40",
    rose: "ring-rose-500/20 hover:ring-rose-500/40",
  }[tone];
  const toneIcon = {
    teal: "bg-teal-400/10 text-teal-300",
    amber: "bg-amber-400/10 text-amber-300",
    rose: "bg-rose-500/10 text-rose-300",
  }[tone];

  return (
    <div
      className={`rounded-2xl border border-slate-800 bg-slate-900/60 p-5 ring-1 ring-inset transition ${toneRing}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${toneIcon}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-mono text-sm font-semibold tracking-wide text-slate-100">{title}</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-slate-400">{description}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, loading, tone = "teal" }) {
  const toneClasses = {
    teal: "bg-teal-500 hover:bg-teal-400 text-slate-950 focus-visible:outline-teal-400",
    rose: "bg-rose-600 hover:bg-rose-500 text-white focus-visible:outline-rose-500",
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-[13px] font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${toneClasses}`}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------
export default function SuperAdminBackupPanel() {
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [loadAdminsError, setLoadAdminsError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const [globalBusy, setGlobalBusy] = useState(false);
  const [chainBackupBusy, setChainBackupBusy] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Historique des sauvegardes
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyType, setHistoryType] = useState("");
  const [historyBusyId, setHistoryBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 5000);
  }, []);

  const loadAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    setLoadAdminsError(null);
    try {
      const res = await apiFetch("/accounts");
      const data = await res.json();
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadAdminsError(e.message);
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // -------------------- Historique des sauvegardes --------------------
  const loadHistory = useCallback(async (page = historyPage, type = historyType) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const params = new URLSearchParams({ page: String(page), size: "10" });
      if (type) params.set("type", type);
      const res = await apiFetch(`/backup?${params.toString()}`);
      const data = await res.json();
      setHistory(data.content || []);
      setHistoryTotalPages(data.totalPages ?? 1);
      setHistoryPage(data.number ?? page);
    } catch (e) {
      setHistoryError(e.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyType]);

  useEffect(() => {
    loadHistory(0, historyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyType]);

  async function handleDownloadHistoryItem(item) {
    setHistoryBusyId(item.id);
    try {
      const res = await apiFetch(`/backup/${item.id}/telecharger`);
      const blob = await res.blob();
      downloadBlob(blob, filenameFromDisposition(res, item.nomFichier || timestampedName("backup")));
    } catch (e) {
      showToast("error", `Échec du téléchargement : ${e.message}`);
    } finally {
      setHistoryBusyId(null);
    }
  }

  async function handleDeleteHistoryItem(item) {
    if (!window.confirm(`Supprimer définitivement la sauvegarde « ${item.nomFichier} » ?`)) return;
    setHistoryBusyId(item.id);
    try {
      await apiFetch(`/backup/${item.id}`, { method: "DELETE" });
      showToast("success", "Sauvegarde supprimée de l'historique.");
      loadHistory(historyPage, historyType);
    } catch (e) {
      showToast("error", `Échec de la suppression : ${e.message}`);
    } finally {
      setHistoryBusyId(null);
    }
  }

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter(
      (a) =>
        (a.nom || "").toLowerCase().includes(q) ||
        (a.prenom || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        String(a.id).includes(q)
    );
  }, [admins, search]);

  const selectedAdmins = useMemo(
    () => admins.filter((a) => selected.has(a.id)),
    [admins, selected]
  );
  const selectedHasActive = selectedAdmins.some((a) => a.actif);

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      const allVisibleSelected = filteredAdmins.every((a) => next.has(a.id));
      filteredAdmins.forEach((a) => (allVisibleSelected ? next.delete(a.id) : next.add(a.id)));
      return next;
    });
  }

  // -------------------- 1) Sauvegarde globale --------------------
  async function handleGlobalBackup() {
    setGlobalBusy(true);
    try {
      const res = await apiFetch("/backup/global");
      const blob = await res.blob();
      downloadBlob(blob, filenameFromDisposition(res, timestampedName("lms_backup_global")));
      showToast("success", "Sauvegarde globale téléchargée.");
      loadHistory(0, historyType);
    } catch (e) {
      showToast("error", `Échec de la sauvegarde globale : ${e.message}`);
    } finally {
      setGlobalBusy(false);
    }
  }

  // -------------------- 2) Sauvegarde en chaîne --------------------
  async function handleChainBackup() {
    if (selected.size === 0) return;
    setChainBackupBusy(true);
    try {
      const res = await apiFetch("/backup/chaine", {
        method: "POST",
        body: JSON.stringify({ adminIds: Array.from(selected) }),
      });
      const blob = await res.blob();
      downloadBlob(blob, filenameFromDisposition(res, timestampedName("lms_backup_chaine")));
      showToast("success", `Sauvegarde en chaîne téléchargée pour ${selected.size} admin(s).`);
      loadHistory(0, historyType);
    } catch (e) {
      showToast("error", `Échec de la sauvegarde en chaîne : ${e.message}`);
    } finally {
      setChainBackupBusy(false);
    }
  }

  // -------------------- 3) Nettoyage en chaîne --------------------
  async function handleConfirmCleanup({ sauvegarderAvant, forcer, supprimerAdmin }) {
    setCleanupBusy(true);
    try {
      const res = await apiFetch("/nettoyage/chaine", {
        method: "POST",
        body: JSON.stringify({
          adminIds: Array.from(selected),
          sauvegarderAvant,
          forcer,
          supprimerAdmin,
        }),
      });
      const data = await res.json();
      setCleanupResult(data);
      setConfirmOpen(false);
      setSelected(new Set());
      loadAdmins();
      loadHistory(0, historyType);
      showToast("success", "Nettoyage en chaîne terminé.");
    } catch (e) {
      showToast("error", `Échec du nettoyage : ${e.message}`);
    } finally {
      setCleanupBusy(false);
    }
  }

  return (
    <div className="min-h-full w-full bg-slate-950 px-4 py-8 text-slate-200 sm:px-8">
      <div className="mx-auto max-w-5xl">
        {/* En-tête */}
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/10 ring-1 ring-inset ring-teal-400/30">
            <DatabaseBackup className="h-5 w-5 text-teal-300" />
          </div>
          <div>
            <h1 className="font-mono text-lg font-semibold text-slate-50">Sauvegarde &amp; nettoyage</h1>
            <p className="text-[13px] text-slate-500">Console SuperAdmin — opérations sur l'ensemble de la plateforme</p>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`mb-6 flex items-start gap-2 rounded-lg border px-3.5 py-2.5 text-[13px] ${
              toast.type === "success"
                ? "border-teal-400/30 bg-teal-400/10 text-teal-200"
                : "border-rose-500/30 bg-rose-500/10 text-rose-200"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>{toast.message}</span>
          </div>
        )}

        {/* Carte 1 : Sauvegarde globale */}
        <div className="mb-6">
          <ActionCard
            icon={Archive}
            tone="teal"
            title="01 · Sauvegarde globale"
            description="Exporte l'intégralité des données de la plateforme (utilisateurs, clients, formations, modules, évaluations, inscriptions, sessions, résultats, licences, OAuth2) dans une archive .zip unique."
          >
            <PrimaryButton onClick={handleGlobalBackup} loading={globalBusy}>
              <Download className="h-3.5 w-3.5" />
              Télécharger la sauvegarde globale
            </PrimaryButton>
          </ActionCard>
        </div>

        {/* Sélection des admins (partagée par 02 et 03) */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60">
          <div className="flex flex-col gap-3 border-b border-slate-800 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-mono text-sm font-semibold tracking-wide text-slate-100">
                Chaîne Admin → Client → Étudiant
              </h3>
              <p className="mt-1 text-[13px] text-slate-400">
                Sélectionnez un ou plusieurs administrateurs pour cibler leur hiérarchie complète.
              </p>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un admin…"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 py-1.5 pl-8 pr-3 text-[13px] text-slate-200 placeholder:text-slate-600 focus:border-teal-400/50 focus:outline-none sm:w-56"
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loadingAdmins ? (
              <div className="flex items-center justify-center gap-2 p-8 text-[13px] text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement des administrateurs…
              </div>
            ) : loadAdminsError ? (
              <div className="p-6 text-[13px] text-rose-300">
                Impossible de charger les administrateurs : {loadAdminsError}
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-slate-500">Aucun administrateur trouvé.</div>
            ) : (
              <table className="w-full text-left text-[13px]">
                <thead className="sticky top-0 bg-slate-900/95 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-10 px-5 py-2">
                      <input
                        type="checkbox"
                        checked={filteredAdmins.length > 0 && filteredAdmins.every((a) => selected.has(a.id))}
                        onChange={toggleAllVisible}
                        className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-400/50"
                      />
                    </th>
                    <th className="px-2 py-2 font-medium">ID</th>
                    <th className="px-2 py-2 font-medium">Nom</th>
                    <th className="px-2 py-2 font-medium">Email</th>
                    <th className="px-2 py-2 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {filteredAdmins.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => toggleOne(a.id)}
                      className={`cursor-pointer transition ${
                        selected.has(a.id) ? "bg-teal-400/5" : "hover:bg-slate-800/40"
                      }`}
                    >
                      <td className="px-5 py-2.5">
                        <input
                          type="checkbox"
                          checked={selected.has(a.id)}
                          onChange={() => toggleOne(a.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-400/50"
                        />
                      </td>
                      <td className="px-2 py-2.5 font-mono text-slate-500">#{a.id}</td>
                      <td className="px-2 py-2.5 text-slate-200">
                        {a.prenom ? `${a.prenom} ${a.nom}` : a.nom}
                      </td>
                      <td className="px-2 py-2.5 text-slate-400">{a.email}</td>
                      <td className="px-2 py-2.5">
                        <StatutBadge actif={a.actif} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-800 p-5 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[13px] text-slate-500">
              {selected.size === 0 ? "Aucun admin sélectionné" : `${selected.size} admin(s) sélectionné(s)`}
            </span>
            <div className="flex flex-wrap gap-2">
              <PrimaryButton onClick={handleChainBackup} disabled={selected.size === 0} loading={chainBackupBusy}>
                <Download className="h-3.5 w-3.5" />
                Sauvegarder la chaîne
              </PrimaryButton>
              <PrimaryButton
                tone="rose"
                onClick={() => setConfirmOpen(true)}
                disabled={selected.size === 0}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Nettoyer la chaîne
              </PrimaryButton>
            </div>
          </div>
        </div>

        {/* Résultat du dernier nettoyage */}
        {cleanupResult && <CleanupResultCard result={cleanupResult} onDismiss={() => setCleanupResult(null)} />}

        {/* Historique des sauvegardes */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/60">
          <div className="flex flex-col gap-3 border-b border-slate-800 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-slate-500" />
              <div>
                <h3 className="font-mono text-sm font-semibold tracking-wide text-slate-100">
                  Historique des sauvegardes
                </h3>
                <p className="mt-0.5 text-[13px] text-slate-400">
                  Archives déjà générées, disponibles au téléchargement sans recalcul.
                </p>
              </div>
            </div>
            <select
              value={historyType}
              onChange={(e) => setHistoryType(e.target.value)}
              className="rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 text-[13px] text-slate-300 focus:border-teal-400/50 focus:outline-none"
            >
              <option value="">Tous les types</option>
              <option value="GLOBALE">Globale</option>
              <option value="CHAINE">Chaîne</option>
              <option value="CHAINE_PRE_NETTOYAGE">Pré-nettoyage</option>
            </select>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-[13px] text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'historique…
            </div>
          ) : historyError ? (
            <div className="p-6 text-[13px] text-rose-300">Impossible de charger l'historique : {historyError}</div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-[13px] text-slate-500">
              Aucune sauvegarde pour le moment — lancez-en une ci-dessus.
            </div>
          ) : (
            <table className="w-full text-left text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-2 font-medium">Date</th>
                  <th className="px-2 py-2 font-medium">Type</th>
                  <th className="px-2 py-2 font-medium">Fichier</th>
                  <th className="px-2 py-2 font-medium">Admins</th>
                  <th className="px-2 py-2 font-medium">Taille</th>
                  <th className="px-5 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {history.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-800/30">
                    <td className="px-5 py-2.5 text-slate-400">{formatDate(item.createdAt)}</td>
                    <td className="px-2 py-2.5">
                      <TypeBadge type={item.type} />
                    </td>
                    <td className="px-2 py-2.5 font-mono text-[12px] text-slate-300">{item.nomFichier}</td>
                    <td className="px-2 py-2.5 text-slate-400">
                      {item.adminIds?.length ? `${item.adminIds.length} admin(s)` : "—"}
                    </td>
                    <td className="px-2 py-2.5 text-slate-400">{formatBytes(item.tailleOctets)}</td>
                    <td className="px-5 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => handleDownloadHistoryItem(item)}
                          disabled={historyBusyId === item.id}
                          title="Télécharger"
                          className="rounded-lg p-1.5 text-teal-300 hover:bg-teal-500/10 disabled:opacity-40"
                        >
                          {historyBusyId === item.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteHistoryItem(item)}
                          disabled={historyBusyId === item.id}
                          title="Supprimer"
                          className="rounded-lg p-1.5 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {historyTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-800 px-5 py-3">
              <button
                onClick={() => loadHistory(Math.max(0, historyPage - 1), historyType)}
                disabled={historyPage === 0}
                className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-200 disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Précédent
              </button>
              <span className="text-[12px] text-slate-500">
                Page {historyPage + 1} / {historyTotalPages}
              </span>
              <button
                onClick={() => loadHistory(Math.min(historyTotalPages - 1, historyPage + 1), historyType)}
                disabled={historyPage >= historyTotalPages - 1}
                className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-200 disabled:opacity-30"
              >
                Suivant <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <CleanupConfirmModal
          admins={selectedAdmins}
          hasActive={selectedHasActive}
          busy={cleanupBusy}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmCleanup}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Modale de confirmation destructive
// ---------------------------------------------------------------------
function CleanupConfirmModal({ admins, hasActive, busy, onCancel, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");
  const [sauvegarderAvant, setSauvegarderAvant] = useState(true);
  const [forcer, setForcer] = useState(false);
  const [supprimerAdmin, setSupprimerAdmin] = useState(false);

  const canConfirm = confirmText.trim().toUpperCase() === "NETTOYER" && (!hasActive || forcer);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-rose-500/30 bg-slate-900 shadow-2xl shadow-rose-950/40">
        <div className="flex items-start gap-3 border-b border-slate-800 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-500/10 text-rose-300">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-mono text-sm font-semibold text-slate-50">Nettoyage définitif de la chaîne</h3>
            <p className="mt-1 text-[13px] text-slate-400">
              Cette action supprime définitivement, pour{" "}
              <span className="font-semibold text-slate-200">{admins.length} administrateur(s)</span> : leurs
              clients, étudiants, formations, modules, évaluations, inscriptions, sessions et résultats.
            </p>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-40 overflow-y-auto border-b border-slate-800 px-5 py-3">
          <ul className="space-y-1 font-mono text-[12px] text-slate-400">
            {admins.map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                <span className="text-slate-600">#{a.id}</span>
                <span className="text-slate-300">{a.prenom ? `${a.prenom} ${a.nom}` : a.nom}</span>
                <StatutBadge actif={a.actif} />
              </li>
            ))}
          </ul>
        </div>

        {hasActive && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-[12.5px] text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Un ou plusieurs admins sélectionnés sont encore <strong>actifs</strong>. Désactivez-les d'abord, ou
              cochez « Forcer » ci-dessous pour passer outre.
            </span>
          </div>
        )}

        <div className="space-y-3 px-5 py-4">
          <label className="flex items-center gap-2 text-[13px] text-slate-300">
            <input
              type="checkbox"
              checked={sauvegarderAvant}
              onChange={(e) => setSauvegarderAvant(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-teal-500 focus:ring-teal-400/50"
            />
            Générer une sauvegarde de sécurité avant suppression (recommandé)
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-300">
            <input
              type="checkbox"
              checked={forcer}
              onChange={(e) => setForcer(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-400/50"
            />
            Forcer même si un admin est encore actif
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-300">
            <input
              type="checkbox"
              checked={supprimerAdmin}
              onChange={(e) => setSupprimerAdmin(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-rose-500 focus:ring-rose-400/50"
            />
            Supprimer également les comptes admin eux-mêmes
          </label>

          <div className="pt-2">
            <label className="mb-1.5 block text-[12px] text-slate-500">
              Tapez <span className="font-mono font-semibold text-rose-300">NETTOYER</span> pour confirmer
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="NETTOYER"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-[13px] text-slate-200 placeholder:text-slate-700 focus:border-rose-400/50 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-800 p-5">
          <button
            onClick={onCancel}
            className="rounded-lg px-3.5 py-2 text-[13px] font-medium text-slate-400 hover:text-slate-200"
          >
            Annuler
          </button>
          <PrimaryButton
            tone="rose"
            disabled={!canConfirm}
            loading={busy}
            onClick={() => onConfirm({ sauvegarderAvant, forcer, supprimerAdmin })}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Confirmer le nettoyage
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Résumé du nettoyage effectué
// ---------------------------------------------------------------------
function CleanupResultCard({ result, onDismiss }) {
  const [open, setOpen] = useState(true);
  const counts = result.compteursSupprimes || {};
  const backup = result.sauvegardePreNettoyage;

  return (
    <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          <h3 className="font-mono text-sm font-semibold text-slate-100">Nettoyage terminé</h3>
        </div>
        <div className="flex items-center gap-3">
          {backup && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500/10 px-2.5 py-1 text-[12px] font-medium text-teal-300">
              <Archive className="h-3 w-3" /> Sauvegarde pré-nettoyage dans l'historique ↓
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-slate-500 transition ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {Object.entries(counts).map(([key, value]) => (
              <div key={key} className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2">
                <div className="font-mono text-lg font-semibold text-slate-100">{value}</div>
                <div className="text-[11px] capitalize text-slate-500">{key}</div>
              </div>
            ))}
          </div>
          <button onClick={onDismiss} className="mt-4 text-[12px] text-slate-500 hover:text-slate-300">
            Masquer ce résumé
          </button>
        </>
      )}
    </div>
  );
}